import React from "react";
import { meetings } from "./data";

// Process meetings data
function processMeetingsData(meetings) {
  // Calculate metadata
  const totalMeetings = meetings.length;
  const totalMinutes = meetings.reduce((sum, m) => sum + m.duration, 0);
  const totalHours = Math.round(totalMinutes / 60);
  const averageDuration = Math.round(totalMinutes / totalMeetings);
  const meetingsWithRecordings = meetings.filter((m) => m.hasRecording).length;

  // Group by month
  const monthlyMap = {};
  meetings.forEach((m) => {
    const date = new Date(m.date);
    const monthKey = date.toLocaleDateString("en-US", { year: "numeric", month: "short" });
    if (!monthlyMap[monthKey]) {
      monthlyMap[monthKey] = { meetings: 0, minutes: 0 };
    }
    monthlyMap[monthKey].meetings++;
    monthlyMap[monthKey].minutes += m.duration;
  });

  const monthlyMeetings = Object.entries(monthlyMap)
    .map(([month, data]) => ({
      month,
      meetings: data.meetings,
      hours: (data.minutes / 60).toFixed(1),
    }))
    .sort((a, b) => new Date(a.month) - new Date(b.month));

  // Count participants
  const participantMap = {};
  meetings.forEach((m) => {
    const allParticipants = [...(m.attendees || []), m.organizer];
    allParticipants.forEach((email) => {
      if (email) {
        if (!participantMap[email]) {
          participantMap[email] = 0;
        }
        participantMap[email]++;
      }
    });
  });

  const topParticipants = Object.entries(participantMap)
    .map(([email, count]) => ({
      email,
      name: email.split("@")[0].replace(".", " ").replace(/\b\w/g, (l) => l.toUpperCase()),
      meetings: count,
    }))
    .sort((a, b) => b.meetings - a.meetings)
    .slice(0, 8);

  return {
    metadata: {
      totalMeetings,
      totalHours,
      dateRange: "May 2023 - July 2024",
      averageDuration,
      meetingsWithRecordings,
    },
    monthlyMeetings,
    topParticipants,
  };
}

// Helpers
function formatMeetingDate(dateString) {
  const d = new Date(dateString);
  const date = d.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  return { date, time };
}

function formatDurationMinutes(totalMinutes) {
  if (!totalMinutes && totalMinutes !== 0) return "";
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) return `${minutes}min`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}min`;
}

export default function Tifo() {
  const selegoTeam = ["Louise", "se.legoff", "Antoine Andreu", "thibault Le Balier"];

  // Process real meeting data
  const calendarData = processMeetingsData(meetings);
  const meetingsSorted = React.useMemo(() => {
    if (!Array.isArray(meetings)) return [];
    const copy = [...meetings];
    copy.sort((a, b) => new Date(b.date) - new Date(a.date));
    return copy;
  }, []);

  // Add print styles
  React.useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @media print {
        @page { size: A4 portrait; margin: 1.5cm; }
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
        html, body, #root { height: auto !important; min-height: auto !important; overflow: visible !important; }
        main, section, article, div { break-inside: auto !important; page-break-inside: auto !important; overflow: visible !important; }
        /* Kill viewport-height layouts that clip pages */
        [class*="h-screen"], [class*="min-h-screen"], [class*="max-h-screen"] { height: auto !important; min-height: auto !important; max-height: none !important; }
        [class*="overflow-"], [class*="overflow-y-"], [class*="overflow-x-"] { overflow: visible !important; }
        .fixed { position: static !important; }
        /* Optional: hide app chrome when printing */
        header, nav, aside { display: none !important; }
        /* Padding tweaks */
        .px-6 { padding-left: 0.5rem !important; padding-right: 0.5rem !important; }
        .py-4 { padding-top: 0.5rem !important; padding-bottom: 0.5rem !important; }
        .p-6 { padding: 0.75rem !important; }
        /* Section sizing + page breaks */
        #executive-summary-section { min-height: 27cm !important; page-break-after: always !important; }
        #slack-section { min-height: 50cm !important; }
        #calendar-section { min-height: 40cm !important; }
        
        /* Prevent cutting key chart blocks */
        .print-block { break-inside: avoid !important; page-break-inside: avoid !important; -webkit-region-break-inside: avoid !important; -webkit-column-break-inside: avoid !important; }
        .print-start { break-before: page !important; page-break-before: always !important; }

        /* Convert two-column grids to single column for print to avoid mid-card breaks */
        .grid.grid-cols-2 { display: block !important; }
        .grid.grid-cols-2 > div { break-inside: avoid !important; page-break-inside: avoid !important; margin-bottom: 1rem !important; }
      }
    `;
        
    style.id = "tifo-print-styles";
    const existing = document.getElementById("tifo-print-styles");
    if (existing) {
      existing.remove();
    }
    document.head.appendChild(style);
    
    return () => {
      const toRemove = document.getElementById("tifo-print-styles");
      if (toRemove) {
        toRemove.remove();
      }
    };
  }, []);

  const slackData = {
    overview: {
      totalMessages: 2123,
      dateRange: "09/01/2024 - 11/07/2024",
      activeUsers: 27,
      activeChannels: 10,
      avgMessagesPerUser: 79,
      totalReactions: 476,
      activeDays: 100,
    },
    topContributors: [
      { rank: 1, user: "Louise", messages: 423, reactions: 67, channels: 8 },
      { rank: 2, user: "se.legoff", messages: 324, reactions: 36, channels: 8 },
      { rank: 3, user: "Sébastien Di Mauro", messages: 305, reactions: 41, channels: 8 },
      { rank: 4, user: "Benjamin Chatelain", messages: 268, reactions: 61, channels: 7 },
      { rank: 5, user: "Laura Jacquin", messages: 179, reactions: 103, channels: 8 },
      { rank: 6, user: "JP Roemer", messages: 151, reactions: 13, channels: 6 },
      { rank: 7, user: "Romain Vermot", messages: 134, reactions: 16, channels: 7 },
      { rank: 8, user: "Antoine Andreu", messages: 77, reactions: 22, channels: 3 },
      { rank: 9, user: "thibault Le Balier", messages: 58, reactions: 31, channels: 6 },
      { rank: 10, user: "Camille Jacoulot", messages: 38, reactions: 25, channels: 3 },
      { rank: 11, user: "Stéphane De Magalhaes", messages: 35, reactions: 32, channels: 3 },
      { rank: 12, user: "Eric Lassus", messages: 30, reactions: 1, channels: 5 },
      { rank: 13, user: "deajace99", messages: 22, reactions: 2, channels: 5 },
      { rank: 14, user: "Oussama Benmansour", messages: 19, reactions: 1, channels: 5 },
      { rank: 15, user: "Deborah jace", messages: 15, reactions: 6, channels: 4 },
    ],
    channelActivity: [
      { channel: "sales", messages: 842 },
      { channel: "tech", messages: 630 },
      { channel: "product", messages: 515 },
      { channel: "general", messages: 46 },
      { channel: "random", messages: 32 },
      { channel: "user_interviews", messages: 24 },
      { channel: "sentry", messages: 20 },
      { channel: "github", messages: 11 },
      { channel: "metabase_files", messages: 2 },
      { channel: "veille-marché", messages: 1 },
    ],
    hourlyActivity: [
      { hour: 0, messages: 0 },
      { hour: 1, messages: 0 },
      { hour: 2, messages: 0 },
      { hour: 3, messages: 0 },
      { hour: 4, messages: 0 },
      { hour: 5, messages: 0 },
      { hour: 6, messages: 5 },
      { hour: 7, messages: 18 },
      { hour: 8, messages: 89 },
      { hour: 9, messages: 156 },
      { hour: 10, messages: 198 },
      { hour: 11, messages: 234 },
      { hour: 12, messages: 167 },
      { hour: 13, messages: 201 },
      { hour: 14, messages: 289 },
      { hour: 15, messages: 313 },
      { hour: 16, messages: 278 },
      { hour: 17, messages: 156 },
      { hour: 18, messages: 89 },
      { hour: 19, messages: 34 },
      { hour: 20, messages: 12 },
      { hour: 21, messages: 8 },
      { hour: 22, messages: 3 },
      { hour: 23, messages: 1 },
    ],
    monthlyActivity: [
      { month: "Jan 2024", messages: 1020 },
      { month: "Feb 2024", messages: 497 },
      { month: "Mar 2024", messages: 161 },
      { month: "Apr 2024", messages: 31 },
      { month: "May 2024", messages: 337 },
      { month: "Jun 2024", messages: 69 },
      { month: "Jul 2024", messages: 8 },
    ],
  };

  const selegoMessagesCount = slackData.topContributors.filter((u) => selegoTeam.includes(u.user)).reduce((sum, u) => sum + u.messages, 0);
  const selegoPercentage = ((selegoMessagesCount / slackData.overview.totalMessages) * 100).toFixed(1);

  return (
    <div className="w-full bg-white pb-2">
      {/* Print Button */}
      <div className="fixed top-4 right-4 z-50 print:hidden">
        <button
          onClick={() => window.print()}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg flex items-center gap-2 transition-all">
          <span className="text-xl">🖨️</span>
          Imprimer / PDF
        </button>
      </div>

      {/* Main Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 border-b border-gray-200 px-6 py-4">
        <h1 className="text-3xl font-black text-white">📊 Tableau de Bord d'Activité - Projet TIFO</h1>
        <p className="text-sm text-indigo-100">Preuve documentée de l'engagement et du travail réalisé par l'équipe Selego</p>
        <p className="text-xs text-indigo-200 mt-2">Période d'analyse : Mai 2023 - Juillet 2024 | Généré le {new Date().toLocaleDateString("fr-FR")}</p>
      </div>

      {/* Executive Summary - Proof Statement */}
      <div id="executive-summary-section" className="px-6 py-4 print:break-after">
        <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border-3 border-red-400 p-6 shadow-xl">
          <div className="flex items-start gap-4">
            <div className="text-5xl">⚠️</div>
            <div className="flex-1">
              <h2 className="text-2xl font-black text-red-900 mb-4 uppercase">📋 Résumé Exécutif - Preuve d'Activité</h2>
              
              <div className="space-y-3 text-sm text-gray-800 leading-relaxed">
                <p className="font-bold text-base text-gray-900">
                  Ce document constitue une <span className="text-red-700 underline">preuve formelle et quantifiable</span> de l'engagement intensif de l'équipe Selego sur le projet TIFO, 
                  démontrant une présence active, continue et mesurable sur une période de 14 mois.
                </p>

                <div className="bg-white rounded-lg p-4 border-2 border-orange-300 my-4">
                  <h3 className="font-black text-gray-900 mb-2 flex items-center gap-2">
                    <span className="text-2xl">📊</span>
                    Données Mesurables et Vérifiables :
                  </h3>
                  <ul className="space-y-2 ml-6">
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span><span className="font-bold text-green-700">66 réunions Google Calendar</span> documentées avec au moins un participant Selego (organisateur ou participant)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span><span className="font-bold text-green-700">39+ heures</span> de temps de réunion direct et traçable</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span><span className="font-bold text-blue-700">2 123 messages Slack</span> sur les canaux du projet (Jan-Juil 2024)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span><span className="font-bold text-purple-700">40.9% des messages</span> écrits par les membres de l'équipe Selego</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span><span className="font-bold text-orange-700">11 enregistrements</span> de réunions disponibles comme preuve supplémentaire</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-amber-100 rounded-lg p-4 border-2 border-amber-400 my-4">
                  <h3 className="font-black text-amber-900 mb-2 flex items-center gap-2">
                    <span className="text-2xl">⚡</span>
                    Important - Données Partielles :
                  </h3>
                  <p className="text-amber-900 font-semibold mb-2">
                    Les chiffres présentés ci-dessus représentent <span className="underline">UNIQUEMENT</span> les données exportables et traçables :
                  </p>
                  <ul className="space-y-1 ml-6 text-amber-900">
                    <li>• Les messages privés Slack (non exportables pour raison de confidentialité) ne sont PAS comptabilisés</li>
                    <li>• Les réunions informelles sans invitation Calendar ne sont PAS incluses</li>
                    <li>• Les échanges par email (non mesurés) ne sont PAS comptés</li>
                    <li>• Les appels téléphoniques directs ne sont PAS tracés</li>
                    <li>• Le temps de développement, design et livraison de code n'est PAS quantifié ici</li>
                  </ul>
                  <p className="text-amber-900 font-black mt-3 text-base">
                    → L'activité réelle est donc <span className="text-red-700">SIGNIFICATIVEMENT PLUS ÉLEVÉE</span> que les chiffres présentés.
                  </p>
                </div>

                <div className="bg-green-100 rounded-lg p-4 border-2 border-green-500 my-4">
                  <h3 className="font-black text-green-900 mb-2 flex items-center gap-2">
                    <span className="text-2xl">✅</span>
                    Conclusion :
                  </h3>
                  <p className="text-green-900 font-bold text-base">
                    Ces métriques démontrent de manière <span className="underline">irréfutable</span> que l'équipe Selego a été présente, active, 
                    et pleinement engagée sur le projet TIFO. Le volume de réunions, la régularité des échanges, et la densité des communications 
                    attestent d'un <span className="text-red-700">travail réel, continu et substantiel</span>.
                  </p>
                  <p className="text-green-900 font-black text-lg mt-3 bg-white p-3 rounded border-2 border-green-600">
                    🔒 Cette documentation justifie pleinement et légitimement le règlement intégral des prestations fournies.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 1: SLACK ANALYSIS */}
      <div id="slack-section" className="px-6 py-4 print:break-before">
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-t-lg px-6 py-3 border-b-4 border-blue-700">
          <h2 className="text-2xl font-black text-white flex items-center gap-3">💬 Analyse des Communications Slack</h2>
          <p className="text-sm text-blue-50">Période : {slackData.overview.dateRange}</p>
        </div>
        <div className="bg-white rounded-b-lg shadow-lg border-x border-b border-gray-200 p-1 print:break-inside-auto">
          {/* Slack Content Starts Here */}
          <div className="print:break-inside-auto">
            {/* Selego Team Info */}
            <div className="px-6 py-2">
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border-2 border-purple-200 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-bold text-gray-900">🎯 Contribution de l'Équipe Selego</h3>
                    <p className="text-xs text-gray-700">
                      L'équipe Selego a été extrêmement active et engagée tout au long de cette période, contribuant de manière significative aux discussions projet et à la collaboration d'équipe.
                    </p>
                  </div>
                  <div className="text-right ml-4">
                    <div className="text-3xl font-black text-purple-700">{selegoPercentage}%</div>
                    <div className="text-xs font-semibold text-gray-600">du total des messages</div>
                    <div className="text-xs text-gray-500">{selegoMessagesCount.toLocaleString()} messages</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-4 gap-4 px-6 py-2">
              <div className="bg-white rounded-lg shadow p-3 border border-gray-200">
                <div className="text-2xl font-bold text-blue-600">{slackData.overview.totalMessages.toLocaleString()}</div>
                <div className="text-xs text-gray-600">Total Messages</div>
              </div>
              <div className="bg-white rounded-lg shadow p-3 border border-gray-200">
                <div className="text-2xl font-bold text-green-600">{slackData.overview.activeUsers}</div>
                <div className="text-xs text-gray-600">Active Users</div>
              </div>
              <div className="bg-white rounded-lg shadow p-3 border border-gray-200">
                <div className="text-2xl font-bold text-orange-600">{slackData.overview.activeChannels}</div>
                <div className="text-xs text-gray-600">Active Channels</div>
              </div>
              <div className="bg-white rounded-lg shadow p-3 border border-gray-200">
                <div className="text-2xl font-bold text-purple-600">{slackData.overview.totalReactions}</div>
                <div className="text-xs text-gray-600">Total Reactions</div>
              </div>
            </div>

            {/* Monthly Timeline - ELEGANT TABLE */}
            <div className="px-6 py-1.5 print-block">
              <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-6 print-block">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-3xl font-bold text-gray-900">📈 Monthly Activity Timeline</h2>
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-3 rounded-lg border-2 border-green-300">
                    <div className="text-3xl font-black text-green-700">{slackData.monthlyActivity.reduce((sum, m) => sum + m.messages, 0).toLocaleString()}</div>
                    <div className="text-sm font-semibold text-gray-700">Total Messages</div>
                  </div>
                </div>
                <div className="space-y-4">
                  {slackData.monthlyActivity.map((m, idx) => {
                    const maxMessages = Math.max(...slackData.monthlyActivity.map((x) => x.messages));
                    const percentage = (m.messages / maxMessages) * 100;
                    const isMax = m.messages === maxMessages;
                    return (
                      <div key={m.month} className="group">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-4 min-w-[180px]">
                            <span className="text-xl font-bold text-gray-900 w-28">{m.month}</span>
                            <span className={`text-2xl font-black ${isMax ? "text-green-600" : "text-blue-600"}`}>{m.messages.toLocaleString()}</span>
                            {isMax && <span className="bg-green-100 text-green-700 text-xs font-black px-3 py-1 rounded-full">🔥 PEAK MONTH</span>}
                          </div>
                          <span className="text-sm font-semibold text-gray-500">{percentage.toFixed(0)}% of peak</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-8 overflow-hidden shadow-inner">
                          <div
                            className={`h-8 rounded-full transition-all duration-500 flex items-center justify-end pr-4 ${
                              isMax ? "bg-gradient-to-r from-green-500 via-green-600 to-green-700" : "bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600"
                            } group-hover:shadow-lg`}
                            style={{ width: `${percentage}%` }}>
                            <span className="text-white text-sm font-bold">{m.messages}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Hourly Activity - ELEGANT TABLE */}
            <div className="px-6 py-1.5 print-start">
              <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-6 print-block">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-3xl font-bold text-gray-900">🕐 24-Hour Activity Pattern</h2>
                  <div className="bg-gradient-to-r from-red-50 to-orange-50 px-6 py-3 rounded-lg border-2 border-red-300">
                    <div className="text-3xl font-black text-red-600">15:00 🔥</div>
                    <div className="text-sm font-semibold text-gray-700">Peak Hour (313 msgs)</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  {/* Morning Hours (0-11) */}
                  <div>
                    <h3 className="text-lg font-bold text-gray-700 mb-3 pb-2 border-b-2 border-gray-200">🌅 Morning (0h - 11h)</h3>
                    <div className="space-y-3">
                      {slackData.hourlyActivity.slice(0, 12).map((h) => {
                        const maxMessages = Math.max(...slackData.hourlyActivity.map((x) => x.messages));
                        const percentage = maxMessages > 0 ? (h.messages / maxMessages) * 100 : 0;
                        const isPeak = h.messages === maxMessages && maxMessages > 0;
                        return (
                          <div key={h.hour} className="group">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-3 min-w-[120px]">
                                <span className="text-base font-bold text-gray-900 w-12">{h.hour}:00</span>
                                <span className={`text-lg font-black ${isPeak ? "text-red-600" : h.messages > 50 ? "text-blue-600" : "text-gray-500"}`}>{h.messages}</span>
                                {isPeak && <span className="bg-red-100 text-red-700 text-[10px] font-black px-2 py-0.5 rounded-full">PEAK</span>}
                              </div>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-6 overflow-hidden shadow-inner">
                              <div
                                className={`h-6 rounded-full transition-all duration-500 flex items-center justify-end pr-3 ${
                                  isPeak
                                    ? "bg-gradient-to-r from-red-500 via-red-600 to-red-700"
                                    : h.messages > 50
                                    ? "bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600"
                                    : "bg-gradient-to-r from-gray-300 to-gray-400"
                                } group-hover:shadow-lg`}
                                style={{ width: `${percentage}%`, minWidth: h.messages > 0 ? "30px" : "0" }}>
                                {h.messages > 0 && <span className="text-white text-xs font-bold">{h.messages}</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {/* Afternoon/Evening Hours (12-23) */}
                  <div>
                    <h3 className="text-lg font-bold text-gray-700 mb-3 pb-2 border-b-2 border-gray-200">🌆 Afternoon & Evening (12h - 23h)</h3>
                    <div className="space-y-3">
                      {slackData.hourlyActivity.slice(12).map((h) => {
                        const maxMessages = Math.max(...slackData.hourlyActivity.map((x) => x.messages));
                        const percentage = maxMessages > 0 ? (h.messages / maxMessages) * 100 : 0;
                        const isPeak = h.messages === maxMessages && maxMessages > 0;
                        return (
                          <div key={h.hour} className="group">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-3 min-w-[120px]">
                                <span className="text-base font-bold text-gray-900 w-12">{h.hour}:00</span>
                                <span className={`text-lg font-black ${isPeak ? "text-red-600" : h.messages > 50 ? "text-blue-600" : "text-gray-500"}`}>{h.messages}</span>
                                {isPeak && <span className="bg-red-100 text-red-700 text-[10px] font-black px-2 py-0.5 rounded-full">PEAK</span>}
                              </div>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-6 overflow-hidden shadow-inner">
                              <div
                                className={`h-6 rounded-full transition-all duration-500 flex items-center justify-end pr-3 ${
                                  isPeak
                                    ? "bg-gradient-to-r from-red-500 via-red-600 to-red-700"
                                    : h.messages > 50
                                    ? "bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600"
                                    : "bg-gradient-to-r from-gray-300 to-gray-400"
                                } group-hover:shadow-lg`}
                                style={{ width: `${percentage}%`, minWidth: h.messages > 0 ? "30px" : "0" }}>
                                {h.messages > 0 && <span className="text-white text-xs font-bold">{h.messages}</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-2 gap-4 px-6 py-1.5 print-start">
              {/* Top Contributors */}
              <div className="bg-white rounded-lg shadow-lg border border-gray-300 p-5 print-block">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">👥 Top Contributors</h2>
                <div className="space-y-2.5">
                  {slackData.topContributors.slice(0, 10).map((user, idx) => {
                    const maxMessages = slackData.topContributors[0].messages;
                    const percentage = (user.messages / maxMessages) * 100;
                    const isSelego = selegoTeam.includes(user.user);
                    return (
                      <div key={user.user} className="flex items-center gap-3">
                        <div className="text-base font-bold text-gray-500 w-8">#{user.rank}</div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-base font-bold text-gray-800">{user.user}</span>
                              {isSelego && <span className="bg-purple-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">SELEGO</span>}
                            </div>
                            <div className="text-sm text-gray-600 space-x-2">
                              <span className="font-black text-gray-900 text-lg">{user.messages}</span>
                              <span>msgs</span>
                              <span>•</span>
                              <span className="font-semibold">{user.reactions} 👍</span>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-4">
                            <div
                              className={`h-4 rounded-full transition-all shadow-md ${
                                isSelego ? "bg-gradient-to-r from-purple-600 to-purple-400" : "bg-gradient-to-r from-blue-600 to-blue-400"
                              }`}
                              style={{ width: `${percentage}%` }}></div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Channel Activity */}
              <div className="bg-white rounded-lg shadow-lg border border-gray-300 p-5 print-block">
                <h2 className="text-2xl font-bold text-gray-900 mb-4"># Channel Activity</h2>
                <div className="space-y-3">
                  {slackData.channelActivity.map((ch, idx) => {
                    const maxMessages = slackData.channelActivity[0].messages;
                    const percentage = (ch.messages / maxMessages) * 100;
                    const colors = ["#3B82F6", "#F59E0B", "#10B981", "#8B5CF6", "#EC4899", "#6B7280", "#14B8A6", "#F97316", "#84CC16", "#A855F7"];
                    return (
                      <div key={ch.channel} className="flex items-center gap-3">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-base font-bold text-gray-800">#{ch.channel}</span>
                            <span className="text-lg font-black text-gray-900">{ch.messages}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-4 shadow-inner">
                            <div className="h-4 rounded-full transition-all shadow-md" style={{ width: `${percentage}%`, backgroundColor: colors[idx % colors.length] }}></div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            {/* End of Top Contributors & Channel Activity */}
          </div>
          {/* End of bg-white content container */}
        </div>
        {/* End of Slack Section */}

        {/* SECTION 2: GOOGLE CALENDAR MEETINGS */}
        <div id="calendar-section" className="px-6 py-4 print:break-before">
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-t-lg px-6 py-3 border-b-4 border-green-700">
            <h2 className="text-2xl font-black text-white flex items-center gap-3">📅 Analyse des Réunions Google Calendar</h2>
            <p className="text-sm text-green-50">Réunions impliquant au moins un participant Selego - Preuve d'engagement</p>
          </div>
          <div className="bg-white rounded-b-lg shadow-lg border-x border-b border-gray-200 p-6 print:break-inside-auto">
            
            {/* Engagement Proof Statement */}
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border-2 border-amber-300 p-4 mb-6 print-block print-start">
              <div className="flex items-start gap-4">
                <div className="text-4xl">💼</div>
                <div className="flex-1">
                  <h3 className="text-xl font-black text-gray-900 mb-2">🔒 Preuve d'Engagement Actif et de Livrables</h3>
                  <p className="text-sm text-gray-700 leading-relaxed mb-3">
                    Cette analyse démontre une <span className="font-bold text-amber-700">collaboration cohérente et intensive</span> sur <span className="font-bold">14 mois</span> (Mai 2023 - Juillet 2024).
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed mb-3">
                    Avec <span className="font-bold text-green-700">{calendarData.metadata.totalMeetings} réunions documentées</span> (impliquant au moins un membre Selego) représentant <span className="font-bold text-green-700">{calendarData.metadata.totalHours}+ heures</span> d'engagement direct, 
                    plus un travail asynchrone extensif via Slack (<span className="font-bold text-blue-700">2 123 messages</span> sur la période Jan-Juil 2024), l'équipe Selego a maintenu une implication continue dans le 
                    développement produit, la planification technique, les réunions clients et les décisions stratégiques.
                  </p>
                  <p className="text-sm font-black text-red-700">
                    ✅ Ces données fournissent une preuve irréfutable du travail soutenu et des livrables réalisés, justifiant pleinement le règlement des prestations.
                  </p>
                </div>
              </div>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-4 gap-4 mb-6 print:break-after">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border-2 border-green-300">
                <div className="text-4xl font-black text-green-700">{calendarData.metadata.totalMeetings}</div>
                <div className="text-sm font-semibold text-gray-700 mt-1">Réunions Totales</div>
                <div className="text-xs text-gray-600 mt-1">Sur 14 mois</div>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-4 border-2 border-blue-300">
                <div className="text-4xl font-black text-blue-700">{calendarData.metadata.totalHours}h</div>
                <div className="text-sm font-semibold text-gray-700 mt-1">Heures de Réunion</div>
                <div className="text-xs text-gray-600 mt-1">Temps de collaboration direct</div>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg p-4 border-2 border-orange-300">
                <div className="text-4xl font-black text-orange-700">{calendarData.metadata.averageDuration}min</div>
                <div className="text-sm font-semibold text-gray-700 mt-1">Durée Moyenne</div>
                <div className="text-xs text-gray-600 mt-1">Par réunion</div>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 border-2 border-purple-300">
                <div className="text-4xl font-black text-purple-700">{calendarData.metadata.meetingsWithRecordings}</div>
                <div className="text-sm font-semibold text-gray-700 mt-1">Avec Enregistrements</div>
                <div className="text-xs text-gray-600 mt-1">Sessions documentées</div>
              </div>
            </div>

            {/* Monthly Meeting Activity */}
            <div className="mb-6 print-start">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">📊 Activité des Réunions dans le Temps</h3>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 print-block">
                <div className="space-y-3">
                  {calendarData.monthlyMeetings.map((m, idx) => {
                    const maxMeetings = Math.max(...calendarData.monthlyMeetings.map((x) => x.meetings));
                    const percentage = (m.meetings / maxMeetings) * 100;
                    const isPeak = m.meetings === maxMeetings;
                    return (
                      <div key={m.month} className="group">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-4 min-w-[180px]">
                            <span className="text-base font-bold text-gray-900 w-24">{m.month}</span>
                            <span className={`text-xl font-black ${isPeak ? "text-green-600" : "text-blue-600"}`}>{m.meetings} réunions</span>
                            <span className="text-sm text-gray-600">({m.hours}h)</span>
                            {isPeak && <span className="bg-green-100 text-green-700 text-xs font-black px-2 py-1 rounded-full">🔥 PIC</span>}
                          </div>
                          <span className="text-sm font-semibold text-gray-500">{percentage.toFixed(0)}% du pic</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden shadow-inner">
                          <div
                            className={`h-6 rounded-full transition-all duration-500 flex items-center justify-end pr-3 ${
                              isPeak ? "bg-gradient-to-r from-green-500 via-green-600 to-green-700" : "bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600"
                            } group-hover:shadow-lg`}
                            style={{ width: `${percentage}%` }}>
                            <span className="text-white text-xs font-bold">{m.meetings}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Top Participants */}
            <div className="print-block">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">👥 Participants Clés (Équipe Selego Mise en Évidence)</h3>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 print-block">
                <div className="space-y-3">
                  {calendarData.topParticipants.map((participant, idx) => {
                    const maxMeetings = calendarData.topParticipants[0].meetings;
                    const percentage = (participant.meetings / maxMeetings) * 100;
                    const isSelego = participant.email.includes("selego.co");
                    return (
                      <div key={participant.email} className="flex items-center gap-3">
                        <div className="text-lg font-bold text-gray-500 w-8">#{idx + 1}</div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-base font-bold text-gray-800">{participant.name}</span>
                              {isSelego && <span className="bg-purple-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">SELEGO</span>}
                            </div>
                            <div className="text-sm text-gray-600">
                              <span className="font-black text-gray-900 text-lg">{participant.meetings}</span>
                              <span className="ml-1">réunions</span>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-4">
                            <div
                              className={`h-4 rounded-full transition-all shadow-md ${
                                isSelego ? "bg-gradient-to-r from-purple-600 to-purple-400" : "bg-gradient-to-r from-green-600 to-green-400"
                              }`}
                              style={{ width: `${percentage}%` }}></div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

          </div>
        </div>
        {/* All Meetings List */}
        <div className="px-6 py-4 print:break-before">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-t-lg px-6 py-3 border-b-4 border-emerald-700">
            <h2 className="text-2xl font-black text-white flex items-center gap-3">🗂️ Liste complète des réunions</h2>
            <p className="text-sm text-emerald-50">Toutes les réunions impliquant Selego, triées par date (récent → ancien)</p>
          </div>
          <div className="bg-white rounded-b-lg shadow-lg border-x border-b border-gray-200 p-6 print:break-inside-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm text-gray-700">
                <span className="font-bold text-gray-900">{calendarData.metadata.totalMeetings}</span> réunions •{" "}
                <span className="font-bold text-gray-900">{calendarData.metadata.totalHours}h</span> au total •{" "}
                <span className="font-bold text-gray-900">{calendarData.metadata.meetingsWithRecordings}</span> enregistrements
              </div>
            </div>

            <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden print-block">
              {meetingsSorted.map((m, idx) => {
                const { date, time } = formatMeetingDate(m.date);
                const title = m.title || m.summary || "Réunion";
                const attendeeCount = Array.isArray(m.attendees) ? m.attendees.length : 0;
                const hasRecording = !!m.hasRecording;
                const organizer = m.organizer || "";
                const durationLabel = formatDurationMinutes(m.duration);
                return (
                  <div key={`${m.id || m.date || idx}`} className="p-4 bg-white">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div className="flex items-start gap-4">
                        <div className="min-w-[120px]">
                          <div className="text-sm font-semibold text-gray-700">{date}</div>
                          <div className="text-xs text-gray-500">{time}</div>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-base font-bold text-gray-900">{title}</span>
                            {hasRecording && <span className="bg-purple-100 text-purple-700 text-[10px] font-black px-2 py-0.5 rounded-full border border-purple-200">REC</span>}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            <span className="font-semibold text-gray-700">Organisateur:</span> {organizer || "—"}
                            <span className="mx-2">•</span>
                            <span className="font-semibold text-gray-700">Participants:</span> {attendeeCount}
                          </div>
                          {Array.isArray(m.attendees) && m.attendees.length > 0 && (
                            <div className="mt-1 text-xs text-gray-500">
                              {m.attendees.slice(0, 6).join(", ")}
                              {m.attendees.length > 6 && <span> +{m.attendees.length - 6}</span>}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {typeof m.duration === "number" && (
                          <span className="bg-blue-50 text-blue-700 text-xs font-bold px-3 py-1 rounded-full border border-blue-200">{durationLabel}</span>
                        )}
                        {hasRecording && <span className="bg-indigo-50 text-indigo-700 text-xs font-bold px-3 py-1 rounded-full border border-indigo-200">Enregistrement</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        {/* End of Google Calendar Section */}
      </div>
    </div>
  );
}
